import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { persistor } from '../redux/store';
import { clearAuth } from '../redux/user/authSlice';
import { SIGNOUT_CHECK_EVERY_MS, noteLogin, signedOutElsewhere } from '../utils/signoutSync';

const SETTLE_MS = 2000;

/**
 * Unified sign-out (utils/signoutSync.js): signs Investigate out when another Scalens app
 * signed out after this page's login started, then sends the user to the Auth0 login
 * page. Checks on load, every 5 s and when the tab comes back into view.
 */
export default function useSignoutSync() {
  const dispatch = useDispatch();
  const { isAuthenticated, getIdTokenClaims, logout, loginWithRedirect } = useAuth0();

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let stopped = false;
    getIdTokenClaims().then((claims) => noteLogin(claims?.iat));
    const check = () => {
      if (stopped || !signedOutElsewhere()) return;
      stopped = true;
      // A page restored with the back button still holds the old login in memory.
      window.addEventListener('pageshow', (event) => event.persisted && window.location.reload());
      // Lets the other app's Auth0 logout finish first: logging in again before it does
      // would silently get the old login back. The SDK is signed out only then, because
      // the protected pages log in again as soon as it is.
      window.setTimeout(async () => {
        // The SDK first, at once (no custom cache): App.js fetches a token as soon as the
        // stored one is cleared, and would get the old one back from a signed-in SDK.
        logout({ localOnly: true });
        localStorage.removeItem('user_token');
        dispatch(clearAuth());
        // Stores the cleared token now: redux-persist writes later, and a page that leaves
        // first would restore the old token after the next login, which then skips
        // fetching a new one.
        await persistor.flush().catch(() => undefined);
        // Protected pages also start a login here, back to the same page; the last one wins.
        const returnTo = window.location.pathname + window.location.search;
        loginWithRedirect({ appState: { returnTo } });
      }, SETTLE_MS);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    check();
    const timer = window.setInterval(check, SIGNOUT_CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [isAuthenticated, getIdTokenClaims, logout, loginWithRedirect, dispatch]);
}
